<script setup lang="ts">
/**
 * A family adjusting their own settings (SPEC §8.8, §6.31).
 *
 * Everything here is a household fact rather than a personal one, so a
 * link-only session may change all of it. How many people usually come and what
 * they cannot eat belong to the family; requiring somebody to prove a phone
 * before correcting a headcount would make the common case the hard one
 * (SPEC §14.5).
 *
 * What does belong to one person — adding or removing a number, notification
 * preferences — is shown and gated rather than hidden, so nobody has to guess
 * whether the product can do it at all.
 */
import { computed, ref, watch } from 'vue'
import { ApiError, endpoints, type HouseholdContactResponse } from '@/api'
import { useFamilyContextStore } from '@/stores/familyContext'

const family = useFamilyContextStore()

const name = ref('')
// Nullable because the number input empties itself while somebody is mid-edit,
// and fighting that on every keystroke is worse than resolving it once on save.
const adults = ref<number | null>(0)
const children = ref<number | null>(0)
const foodNotes = ref('')

const contacts = ref<HouseholdContactResponse[]>([])

/*
  Adding an email address (ADR-016, MT-182).

  The confirmation is not immediate and cannot be: somebody has to open their
  mailbox. So the screen says what was sent and where, and the contact appears
  in the list above only once it has been proved.
*/
const newEmail = ref('')
const newEmailName = ref('')
const addingEmail = ref(false)
const emailError = ref<string | null>(null)
const emailSent = ref<string | null>(null)

async function addEmail(): Promise<void> {
  emailError.value = null
  emailSent.value = null
  addingEmail.value = true

  try {
    const accepted = await endpoints.addFamilyEmail({
      email: newEmail.value.trim(),
      displayName: newEmailName.value.trim() || undefined,
    })

    // The masked form the server sends back, not what was typed. It is the
    // same shape the list above shows, so the two read as one thing.
    emailSent.value = accepted.maskedEmail
    newEmail.value = ''
    newEmailName.value = ''
  } catch (caught) {
    emailError.value =
      caught instanceof ApiError && caught.problem?.errors?.Email?.[0]
        ? caught.problem.errors.Email[0]
        : 'We could not send that just then. Try again in a moment.'
  } finally {
    addingEmail.value = false
  }
}
const loading = ref(true)
const saving = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

const canRename = computed(() => family.group?.allowHouseholdRename === true)

function reset() {
  name.value = family.household?.name ?? ''
  // Coerced, because the generated contract types an int32 as number | string
  // and the number input will only take one of those.
  adults.value = Number(family.household?.defaultAdultCount ?? 0)
  children.value = Number(family.household?.defaultChildCount ?? 0)
  foodNotes.value = family.household?.foodNotes ?? ''
}

async function load() {
  loading.value = true

  try {
    await family.ensureLoaded()
    reset()
    contacts.value = await endpoints.familyContacts()
  } catch (caught) {
    error.value = (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  error.value = null
  notice.value = null
  fieldErrors.value = {}

  try {
    const context = await endpoints.updateFamilyHousehold({
      // Only what this screen is allowed to change. Sending a name the group
      // does not permit would be refused, which is right, but there is no
      // reason to ask.
      name: canRename.value ? name.value.trim() : null,
      defaultAdultCount: adults.value ?? 0,
      defaultChildCount: children.value ?? 0,
      foodNotes: foodNotes.value.trim(),
    })

    family.adopt(context)
    reset()
    notice.value = 'Saved.'
  } catch (caught) {
    if (caught instanceof ApiError && caught.problem?.errors) {
      fieldErrors.value = caught.problem.errors
    }

    error.value = (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
  } finally {
    saving.value = false
  }
}

watch(() => family.householdId, load, { immediate: true })
</script>

<template>
  <v-container class="settings" max-width="40rem">
    <h1 class="settings__title">Your household</h1>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="notice" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>

    <v-skeleton-loader v-if="loading" type="article" />

    <template v-else>
      <form @submit.prevent="save">
        <v-text-field
          v-model="name"
          label="Household name"
          :disabled="!canRename || saving"
          :error-messages="fieldErrors.Name"
          :hint="
            canRename
              ? 'What your family is called on the list.'
              : 'Whoever organizes your meals looks after household names.'
          "
          persistent-hint
          class="mb-4"
        />

        <p class="settings__lead">
          How many of you usually come. These are a starting point — you can say something different
          for any meal.
        </p>

        <div class="settings__counts">
          <v-number-input
            v-model="adults"
            label="Adults"
            :min="0"
            :disabled="saving"
            :error-messages="fieldErrors.DefaultAdultCount"
            control-variant="split"
          />
          <v-number-input
            v-model="children"
            label="Kids"
            :min="0"
            :disabled="saving"
            :error-messages="fieldErrors.DefaultChildCount"
            control-variant="split"
          />
        </div>

        <v-textarea
          v-model="foodNotes"
          label="Anything the cook should know"
          placeholder="Allergies, or what the kids will not touch."
          rows="3"
          :disabled="saving"
          hint="Seen by whoever organizes your meals and by your household. Nowhere else."
          persistent-hint
          class="mb-4"
        />

        <v-btn type="submit" variant="flat" color="primary" :loading="saving"> Save changes </v-btn>
      </form>

      <v-divider class="my-8" />

      <section>
        <h2 class="settings__section">Who is connected</h2>

        <p v-if="contacts.length === 0" class="settings__lead">
          Nobody has confirmed an email address yet. Your link still works for everybody who has it.
        </p>

        <ul v-else class="settings__contacts">
          <li v-for="contact in contacts" :key="contact.id" class="settings__contact">
            <span>
              <strong>{{ contact.displayName ?? 'Someone' }}</strong>
              <!--
                Masked, never the whole thing (SPEC §14.4, §19.1). A contact may
                now hold an address, a number, or both, and null means there is
                none rather than one somebody may not see.
              -->
              <span v-if="contact.maskedEmail" class="settings__channel">
                {{ contact.maskedEmail }}
              </span>
              <span v-if="contact.maskedPhone" class="settings__channel">
                {{ contact.maskedPhone }}
              </span>
            </span>
            <v-chip v-if="contact.isPrimary" size="x-small" variant="tonal">First to reach</v-chip>
          </li>
        </ul>

        <!--
          Adding an address, since ADR-016 made email the way a family is told
          anything. Open to anybody holding the household link, which is the
          same bar as everything else on this screen — and the address still has
          to prove itself before a single message goes to it.
        -->
        <form class="settings__add" @submit.prevent="addEmail">
          <h3 class="settings__subsection">Add an email address</h3>

          <p class="settings__lead">
            We will send it one message asking whether it is yours. Nothing else goes there until
            somebody says yes.
          </p>

          <v-text-field
            v-model="newEmail"
            label="Email address"
            name="email"
            type="email"
            autocomplete="email"
            :error-messages="emailError ? [emailError] : []"
            :disabled="addingEmail"
          />

          <v-text-field
            v-model="newEmailName"
            label="Who is it for?"
            name="displayName"
            placeholder="Gran"
            :disabled="addingEmail"
          />

          <v-btn type="submit" color="primary" :loading="addingEmail">Send the check</v-btn>

          <v-alert
            v-if="emailSent"
            type="success"
            variant="tonal"
            density="comfortable"
            class="mt-4"
            data-testid="email-sent"
          >
            Sent to {{ emailSent }}. It will show up here once somebody confirms it.
          </v-alert>
        </form>
      </section>
    </template>
  </v-container>
</template>

<style scoped>
.settings {
  padding-top: 2rem;
  padding-bottom: 3rem;
}

.settings__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem;
}

.settings__section {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.settings__lead {
  margin: 0 0 1rem;
  max-width: 48ch;
}

.settings__counts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.settings__subsection {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.settings__add {
  margin-top: 2rem;
}

.settings__channel {
  display: block;
  font-size: 0.875rem;
  opacity: 0.75;
}

.settings__contacts {
  list-style: none;
  margin: 0;
  padding: 0;
}

.settings__contact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.5rem 0;
}

.settings__contact + .settings__contact {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.settings__phone {
  margin-left: 0.5rem;
}
</style>
